import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext, useSelectedBroker } from '../contexts/hooks';
import { buildAccountsFromApiData } from '../lib/accountData';
import * as api from '../services/orderly-api';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
let nextAccountDataRequestId = 0;

export function createAccountDataRequestBarrier(): number {
  return ++nextAccountDataRequestId;
}

export function useAccountData() {
  const { state, dispatch } = useAppContext();
  const broker = useSelectedBroker();
  const brokerId = broker?.id ?? null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKey = useRef<string | null>(null);
  const requestSequence = useRef(0);
  const latestRequest = useRef<{ key: string; sequence: number; sessionGeneration: number } | null>(null);
  const sessionGenerationRef = useRef(state.sessionGeneration);
  sessionGenerationRef.current = state.sessionGeneration;
  const auth = state.authContext?.brokerId === brokerId ? state.authContext : null;

  const fetchAccountData = useCallback(async () => {
    if (USE_MOCK || !auth || !brokerId || !state.walletAddress || !state.chain) return;

    const walletAddress = state.walletAddress;
    const chain = state.chain;
    const requestKey = `${walletAddress}:${chain}:${brokerId}:${auth.accountId}:${auth.orderlyKey}`;
    const sessionGeneration = state.sessionGeneration;
    const requestId = createAccountDataRequestBarrier();
    const sequence = ++requestSequence.current;
    latestRequest.current = { key: requestKey, sequence, sessionGeneration };
    setLoading(true);
    setError(null);
    const isLatestRequest = () => {
      const latest = latestRequest.current;
      return latest?.key === requestKey
        && latest.sequence === sequence
        && latest.sessionGeneration === sessionGeneration
        && sessionGenerationRef.current === sessionGeneration;
    };
    try {
      const [futures, fundingRates] = await Promise.all([
        api.getFuturesInfo(),
        api.getFundingRates(),
      ]);

      const subAccounts = await api.getSubAccounts(auth);
      let fallbackSubAccountIds: string[];
      try {
        const allBrokerAccounts = await api.getAllAccounts(walletAddress, brokerId);
        fallbackSubAccountIds = allBrokerAccounts
          .filter((account) => account.user_type === 'SUB')
          .map((account) => account.account_id);
      } catch (allAccountsError) {
        console.warn('Failed to fetch full broker account list:', allAccountsError);
        fallbackSubAccountIds = (broker?.accounts ?? [])
          .filter((account) => account.type === 'sub')
          .map((account) => account.accountId);
      }
      const [positions, holdings] = await Promise.all([
        api.getAggregatePositions(auth),
        api.getAggregateHolding(auth),
      ]);
      const [orders, algoOrders] = await Promise.all([
        api.getOpenOrders(auth),
        api.getOpenAlgoOrders(auth),
      ]);

      const subAccountRows = subAccounts.rows ?? [];
      const subAccountIdSet = new Set(subAccountRows.map((account) => account.sub_account_id));
      const fallbackSubAccounts = fallbackSubAccountIds
        .filter((accountId) => accountId !== auth.accountId && !subAccountIdSet.has(accountId))
        .map((accountId) => ({
          sub_account_id: accountId,
          description: '',
        }));

      const accounts = buildAccountsFromApiData({
        authAccountId: auth.accountId,
        subAccounts: [...subAccountRows, ...fallbackSubAccounts],
        futures: futures.rows ?? [],
        fundingRates: fundingRates.rows ?? [],
        positions: positions.rows ?? [],
        holdings: holdings.rows ?? [],
        orders: orders.rows ?? [],
        algoOrders: algoOrders.rows ?? [],
      });

      if (!isLatestRequest()) return;

      dispatch({
        type: 'UPDATE_ACCOUNT_DATA',
        sessionGeneration,
        requestId,
        brokerId,
        accounts,
        walletAddress,
        chain,
        authAccountId: auth.accountId,
        orderlyKey: auth.orderlyKey,
      });
    } catch (err) {
      if (!isLatestRequest()) return;
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
      console.warn('Failed to fetch account data:', err);
    } finally {
      if (isLatestRequest()) setLoading(false);
    }
  }, [auth, state.walletAddress, state.chain, state.sessionGeneration, broker, brokerId, dispatch]);

  useEffect(() => {
    requestSequence.current += 1;
    latestRequest.current = null;
    lastFetchKey.current = null;
    setLoading(false);
    setError(null);
  }, [state.sessionGeneration]);

  // Fetch once on mount (when auth is ready)
  useEffect(() => {
    const fetchKey = auth && brokerId && state.walletAddress && state.chain
      ? `${state.sessionGeneration}:${state.page}:${state.walletAddress}:${state.chain}:${brokerId}:${auth.accountId}:${auth.orderlyKey}`
      : null;
    if (!fetchKey || lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;
    fetchAccountData();
  }, [state.sessionGeneration, state.page, auth, state.walletAddress, state.chain, brokerId, fetchAccountData]);

  useEffect(() => {
    if (!brokerId || !auth) {
      lastFetchKey.current = null;
      requestSequence.current += 1;
      latestRequest.current = null;
      setLoading(false);
    }
  }, [brokerId, auth]);

  return { loading, error, clearError: () => setError(null), refetch: fetchAccountData };
}
